import { useContext, useEffect, useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import { billingAllows } from "../../billingAccess";
import { Button, Input, SectionHeader, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { formatSar } from "../../../../utils/billingMoney";
import {
  addSubscriptionBranchAPI,
  extendAllSubscriptionBranchesAPI,
  extendSubscriptionBranchAPI,
  getInvoiceAPI,
  getSubscriptionHistoryAPI,
  getSubscriptionsLedgerAPI,
  getSubscriptionsOverviewAPI,
  stopAllSubscriptionBranchesAPI,
  stopSubscriptionBranchAPI,
} from "../../../../utils/api/billing";
import CreateSubscriptionModal from "../modals/CreateSubscriptionModal";
import ChangePlanModal from "../modals/ChangePlanModal";
import AddBranchModal from "../modals/AddBranchModal";
import ExtendCoverageModal from "../modals/ExtendCoverageModal";
import StopCoverageModal from "../modals/StopCoverageModal";
import InvoiceDetailModal from "../modals/InvoiceDetailModal";
import { useBillingAdminText } from "../billingAdminI18n";
import ReconciliationPanel from "./ReconciliationPanel";
import GrantSubscriptionModal from "../modals/GrantSubscriptionModal";
import GrantActionModal from "../modals/GrantActionModal";
import { grantText, originTagOf } from "../grantText";

const OVERVIEW_KEYS = [
  "totalCustomers",
  "totalBranches",
  "ludSubscribedBranches",
  "ludNotSubscribedCustomers",
  "ludStoppedCustomers",
  "intgSubscribedBranches",
  "intgNotSubscribedCustomers",
  "intgStoppedCustomers",
  "ludInactiveBranches",
  "intgInactiveBranches",
];

const ORIGIN_VARIANT = { paid: "success", converted: "success", converting: "warning", granted: "primary", pending_start: "primary", expired: "danger", pending_payment: "warning" };
const STATUS_VARIANT = { active: "success", draft: "warning", stopped: "danger", none: "neutral" };
const INVOICE_BUCKET_VARIANT = { awaiting_payment: "warning", finance_review: "warning", paid: "success", cancelled: "danger" };

const formatUtcDay = (value, lang) => (value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", { timeZone: "UTC" }) : "-"); // billing days are UTC days: an end date of 23:59 UTC must not read as the next day in local time
const formatDate = (value, lang) => (value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US") : "-");

const statusLabel = (status, text) => {
  if (status === "active") return text.subscriptions.ledgerLabels.statusActive;
  if (status === "draft") return text.subscriptions.ledgerLabels.statusDraft;
  if (status === "stopped") return text.subscriptions.ledgerLabels.statusStopped;
  return text.subscriptions.ledgerLabels.statusNone;
};

const BranchCoverageRow = ({ branch, text, lang, canChange, canCancel, onExtend, onStop }) => (
  <div className="billing-sub-branch-row">
    <span className="billing-sub-branch-name">{branch.name}</span>
    <span className="billing-sub-branch-dates">
      {formatDate(branch.startDate, lang)} → {formatDate(branch.endDate, lang)}
    </span>
    <div className="billing-sub-branch-row-actions">
      {canChange && (
        <Button size="sm" appearance="outline" onClick={() => onExtend(branch)}>
          {text.subscriptions.ledgerLabels.extendBranch}
        </Button>
      )}
      {canCancel && (
        <Button size="sm" appearance="outline" onClick={() => onStop(branch)}>
          {text.subscriptions.ledgerLabels.stopBranch}
        </Button>
      )}
    </div>
  </div>
);

const PendingBranchRow = ({ branch, text, lang }) => (
  <div className="billing-sub-branch-row">
    <span className="billing-sub-branch-name">{branch.name}</span>
    <span className="billing-sub-branch-dates">
      {formatDate(branch.startDate, lang)} → {formatDate(branch.endDate, lang)}
    </span>
    <Tags variant="warning">{text.subscriptions.ledgerLabels.awaitingPayment}</Tags>
  </div>
);

const InvoiceActionRow = ({ invoice, text, lang, onView }) => (
  <div className="billing-sub-branch-row">
    <span className="billing-sub-branch-name">
      {invoice.invoiceNumber || text.invoices.draft}
      {" · "}
      <Tags variant={INVOICE_BUCKET_VARIANT[invoice.bucket] || "neutral"}>{text.invoices.statusCards[invoice.bucket] || invoice.bucket}</Tags>
    </span>
    <span className="billing-sub-branch-dates">{formatSar(invoice.balanceDueMinor ?? invoice.totalMinor, lang)}</span>
    <div className="billing-sub-branch-row-actions">
      <Button size="sm" appearance="outline" onClick={() => onView(invoice._id)}>
        {text.subscriptions.ledgerLabels.viewInvoice}
      </Button>
    </div>
  </div>
);

const pendingSubscriptionPeriod = (subscription) => {
  const dates = (subscription?.branchCoverage || []).flatMap((branch) => [branch.startDate, branch.endDate]).filter(Boolean).map((value) => new Date(value));
  if (!dates.length) return { start: null, end: null };
  return { start: new Date(Math.min(...dates)), end: new Date(Math.max(...dates)) };
};

const ServiceBlock = ({
  label,
  block,
  text,
  lang,
  onChangePlan,
  onAddBranch,
  onExtendBranch,
  onStopBranch,
  onHistory,
  onCreateSubscription,
  onViewInvoice,
  canManage,
  canChange,
  canCancel,
  canGrant = false,
  onGrant,
  onGrantAction,
  grantable = false,
}) => {
  const pendingPeriod = pendingSubscriptionPeriod(block.pendingSubscription);
  const gt = grantText(lang);
  const origin = originTagOf(block);
  const isGrant = block.currentSubscription?.origin === "admin_grant" || Boolean(block.currentSubscription?.grant?.conversionPending);
  const isRunningGrant = block.currentSubscription?.origin === "admin_grant";
  const originLabel = (origin === "granted" || origin === "converting") && block.currentSubscription?.grant?.endDate ? `${origin === "granted" ? gt.grantedUntil : gt.converting} ${formatUtcDay(block.currentSubscription.grant.endDate, lang)}` : origin ? gt[{ pending_start: "pendingStart", pending_payment: "pendingPayment" }[origin] || origin] : null;
  return (
    <div className="billing-sub-service-block">
      <div className="billing-sub-service-head">
        <strong>{label}</strong>
        <Tags variant={STATUS_VARIANT[block.status] || "neutral"}>{statusLabel(block.status, text)}</Tags>
        {originLabel && (
          <Tags variant={ORIGIN_VARIANT[origin] || "neutral"} title={origin}>{originLabel}</Tags>
        )}
      </div>

      {canGrant && grantable && !block.currentSubscription && !block.pendingSubscription && (
        <div className="billing-sub-service-actions">
          <Button size="sm" appearance="outline" onClick={onGrant}>{gt.action}</Button>
        </div>
      )}

      {block.status === "none" && canManage && (
        <div className="billing-sub-service-actions">
          <Button size="sm" appearance="filled" onClick={onCreateSubscription}>
            {text.subscriptions.create}
          </Button>
        </div>
      )}

      {block.pendingSubscription && (
        <div className="billing-sub-pending-panel">
          <div className="billing-version-groups">
            <div>
              <span>{text.subscriptions.pending.package}</span>
              <strong>
                {block.pendingSubscription.planSnapshot?.code} v{block.pendingSubscription.planSnapshot?.version}
              </strong>
            </div>
            <div>
              <span>{text.subscriptions.pending.branches}</span>
              <strong>{(block.pendingSubscription.branchCoverage || []).map((branch) => branch.branchName).join("، ") || "-"}</strong>
            </div>
            <div>
              <span>{text.subscriptions.pending.period}</span>
              <strong>
                {formatDate(pendingPeriod.start, lang)} → {formatDate(pendingPeriod.end, lang)}
              </strong>
            </div>
          </div>
          <div className="billing-empty-note">{text.subscriptions.pending.requirement}</div>
        </div>
      )}

      {block.branchSplit.subscribed.length > 0 && (
        <div className="billing-sub-branch-list">
          <span className="billing-sub-branch-list-title">{text.subscriptions.ledgerLabels.subscribedBranches}</span>
          {block.branchSplit.subscribed.map((branch) => (
            <BranchCoverageRow
              key={branch.branchId}
              branch={branch}
              text={text}
              lang={lang}
              canChange={canChange && !isGrant}
              canCancel={canCancel && !isGrant}
              onExtend={(selectedBranch) => onExtendBranch(block.currentSubscription, selectedBranch)}
              onStop={(selectedBranch) => onStopBranch(block.currentSubscription, selectedBranch)}
            />
          ))}
        </div>
      )}

      {block.branchSplit.pending?.length > 0 && (
        <div className="billing-sub-branch-list">
          <span className="billing-sub-branch-list-title">{text.subscriptions.ledgerLabels.pendingBranches}</span>
          {block.branchSplit.pending.map((branch) => (
            <PendingBranchRow key={branch.branchId} branch={branch} text={text} lang={lang} />
          ))}
        </div>
      )}

      {block.branchSplit.notSubscribed.length > 0 && (
        <div className="billing-sub-branch-list">
          <span className="billing-sub-branch-list-title">{text.subscriptions.ledgerLabels.notSubscribedBranches}</span>
          {block.branchSplit.notSubscribed.map((branch) => (
            <div key={branch.branchId} className="billing-sub-branch-row">
              <span className="billing-sub-branch-name">{branch.name}</span>
              {canChange && block.currentSubscription && !isGrant && (
                <div className="billing-sub-branch-row-actions">
                  <Button size="sm" appearance="outline" onClick={() => onAddBranch(block.currentSubscription, branch)}>
                    {text.subscriptions.ledgerLabels.addBranch}
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {block.unpaidInvoices?.length > 0 && (
        <div className="billing-sub-branch-list">
          <span className="billing-sub-branch-list-title">{text.subscriptions.ledgerLabels.unpaidInvoicesTitle}</span>
          {block.unpaidInvoices.map((invoice) => (
            <InvoiceActionRow key={invoice._id} invoice={invoice} text={text} lang={lang} onView={onViewInvoice} />
          ))}
        </div>
      )}

      {(block.currentSubscription || block.pendingSubscription) && (
        <div className="billing-sub-service-actions">
          {canChange && !isGrant && block.currentSubscription?.status === "active" && (
            <Button size="sm" appearance="ghost" onClick={() => onChangePlan(block.currentSubscription)}>
              {text.subscriptions.changePlan}
            </Button>
          )}
          {canGrant && isGrant && block.currentSubscription?.status === "active" && (
            <>
              {isRunningGrant && <Button size="sm" appearance="outline" onClick={() => onGrantAction("extend", block.currentSubscription)}>{gt.extend}</Button>}
              {isRunningGrant && <Button size="sm" appearance="outline" onClick={() => onGrantAction("convert", block.currentSubscription)}>{gt.convert}</Button>}
              <Button size="sm" appearance="outline" onClick={() => onGrantAction("cancel", block.currentSubscription)}>{gt.cancelGrant}</Button>
            </>
          )}
          <Button size="sm" appearance="ghost" onClick={() => onHistory(block.currentSubscription || block.pendingSubscription)}>
            {text.subscriptions.ledgerLabels.viewHistory}
          </Button>
        </div>
      )}
    </div>
  );
};

const allActiveBranches = (row, text) => [
  ...row.lud.branchSplit.subscribed.map((branch) => ({
    branchId: `${branch.branchId}-lud`,
    name: `${branch.name} (${text.subscriptions.ledgerLabels.ludStatus})`,
    currentEndDate: branch.endDate,
  })),
  ...row.integration.branchSplit.subscribed.map((branch) => ({
    branchId: `${branch.branchId}-intg`,
    name: `${branch.name} (${text.subscriptions.ledgerLabels.intgStatus})`,
    currentEndDate: branch.endDate,
  })),
];

const hasActiveBranches = (row) => (row.lud.currentSubscription?.origin !== "admin_grant" && !row.lud.currentSubscription?.grant?.conversionPending && row.lud.branchSplit.subscribed.length > 0) || row.integration.branchSplit.subscribed.length > 0;

const SubscriptionsTab = ({ access = null } = {}) => {
  const { lang = "en" } = useContext(Context) || {};
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const canView = billingAllows(can("billing.admin.view"), access?.canViewBillingAdmin);
  const canManage = billingAllows(can("billing.subscription.create"), access?.canCreateSubscription);
  const canChange = billingAllows(can("billing.subscription.change"), access?.canChangeSubscription);
  const canCancel = billingAllows(can("billing.subscription.cancel"), access?.canCancelSubscription);
  const canGrant = can("billing.subscription.grant");
  const gtext = grantText(lang);

  const [overview, setOverview] = useState(null);
  const [ledger, setLedger] = useState({ data: [], pagination: { page: 1, pages: 1, total: 0 } });
  const [search, setSearch] = useState("");
  const [openRows, setOpenRows] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);
  const [historyPanel, setHistoryPanel] = useState(null);
  const [detailInvoice, setDetailInvoice] = useState(null);

  const load = async (overrides = {}) => {
    if (!canView || !token) return;
    setLoading(true);
    setError("");
    try {
      const [overviewResult, ledgerResult] = await Promise.all([
        getSubscriptionsOverviewAPI(token),
        getSubscriptionsLedgerAPI(token, { search, page: 1, ...overrides }),
      ]);
      setOverview(overviewResult.data);
      setLedger(ledgerResult);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const toggleRow = (storeId) => {
    setOpenRows((current) => {
      const next = new Set(current);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const openHistory = async (subscription) => {
    setHistoryPanel({ loading: true, rows: [] });
    try {
      const result = await getSubscriptionHistoryAPI(token, subscription._id);
      setHistoryPanel({ loading: false, rows: result.data || [] });
    } catch (err) {
      setHistoryPanel({ loading: false, rows: [], error: err.message });
    }
  };

  const openInvoice = async (invoiceId) => {
    setError("");
    try {
      const result = await getInvoiceAPI(token, invoiceId);
      setDetailInvoice(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  const addBranch = async ({ startDate, endDate }) => {
    // handler-level authorization
    if (!canChange) return;
    const { subscription, branch } = modal;
    await addSubscriptionBranchAPI(token, subscription._id, branch.branchId, { startDate, endDate });
    setModal(null);
    await load();
  };

  const extendBranch = async (endDate) => {
    // handler-level authorization
    if (!canChange) return;
    const { subscription, branch } = modal;
    await extendSubscriptionBranchAPI(token, subscription._id, branch.branchId, { endDate });
    setModal(null);
    await load();
  };

  const stopBranch = async () => {
    // handler-level authorization
    if (!canCancel) return;
    const { subscription, branch } = modal;
    await stopSubscriptionBranchAPI(token, subscription._id, branch.branchId, { reason: text.subscriptions.adminRequested });
    setModal(null);
    await load();
  };

  const extendAll = async (endDate) => {
    // handler-level authorization
    if (!canChange) return;
    const { row } = modal;
    await extendAllSubscriptionBranchesAPI(token, row.storeId, { endDate });
    setModal(null);
    await load();
  };

  const stopAll = async () => {
    // handler-level authorization
    if (!canCancel) return;
    const { row } = modal;
    await stopAllSubscriptionBranchesAPI(token, row.storeId, { reason: text.subscriptions.adminRequested });
    setModal(null);
    await load();
  };

  if (!canView) {
    return <div className="billing-admin-denied">{text.subscriptions.denied}</div>;
  }

  return (
    <section className="billing-subscriptions-v141">
      <SectionHeader
        title={text.subscriptions.title}
        description={text.subscriptions.global}
        actions={
          <>
            {canManage && (
              <Button appearance="filled" onClick={() => setModal({ type: "create", subType: "platform" })}>
                {text.subscriptions.create}
              </Button>
            )}
            {canGrant && (
              <Button appearance="outline" onClick={() => setModal({ type: "grant" })}>
                {gtext.action}
              </Button>
            )}
          </>
        }
      />

      {error && <div className="billing-error">{error}</div>}

      <div className="billing-kpi-row billing-kpi-row-wrap">
        {OVERVIEW_KEYS.map((key) => (
          <div className="billing-kpi-card" key={key}>
            <span>{text.subscriptions.overview[key]}</span>
            <strong>{overview?.[key] ?? 0}</strong>
            <small>{text.subscriptions.overview[`${key}Hint`]}</small>
          </div>
        ))}
      </div>

      <div className="billing-filter-bar">
        <Input
          label={text.common.search}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") load({ search });
          }}
          placeholder={text.subscriptions.ledgerLabels.searchPlaceholder}
        />
        <Button appearance="outline" onClick={() => load({ search })}>
          {text.common.apply}
        </Button>
      </div>

      {loading ? (
        <div className="billing-loading">{text.common.loading}</div>
      ) : ledger.data.length === 0 ? (
        <div className="billing-empty-note">{text.subscriptions.ledgerLabels.empty}</div>
      ) : (
        ledger.data.map((row) => (
          <div key={row.storeId} className={`billing-ledger-group ${openRows.has(row.storeId) ? "open" : ""}`}>
            <button type="button" className="billing-ledger-group-head" onClick={() => toggleRow(row.storeId)}>
              <div>
                <strong>{row.restaurant.businessName || row.storeId}</strong>
                <span className="billing-ledger-group-sub">
                  {text.subscriptions.ledgerLabels.accountId}: {row.storeId} · {text.subscriptions.ledgerLabels.branchCount}: {row.branchCount}
                </span>
              </div>
              <div className="billing-sub-chip-row">
                <Tags variant={STATUS_VARIANT[row.lud.status] || "neutral"}>LUD</Tags>
                <Tags variant={STATUS_VARIANT[row.integration.status] || "neutral"}>{text.subscriptions.ledgerLabels.intgStatus}</Tags>
              </div>
            </button>
            {openRows.has(row.storeId) && (
              <div className="billing-ledger-group-body">
                <div className="billing-sub-restaurant-actions">
                  {canChange && (
                    <Button
                      size="sm"
                      appearance="outline"
                      disabled={!hasActiveBranches(row)}
                      onClick={() => setModal({ type: "extendAll", row })}
                    >
                      {text.subscriptions.extendAll}
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      size="sm"
                      appearance="outline"
                      disabled={!hasActiveBranches(row)}
                      onClick={() => setModal({ type: "stopAll", row })}
                    >
                      {text.subscriptions.stopAll}
                    </Button>
                  )}
                </div>
                <ServiceBlock
                  label={text.subscriptions.ledgerLabels.ludStatus}
                  block={row.lud}
                  text={text}
                  lang={lang}
                  canManage={canManage}
                  canChange={canChange}
                  canCancel={canCancel}
                  onChangePlan={(subscription) => setModal({ type: "changePlan", subscription })}
                  onAddBranch={(subscription, branch) => setModal({ type: "addBranch", subscription, branch })}
                  onExtendBranch={(subscription, branch) => setModal({ type: "extendBranch", subscription, branch })}
                  onStopBranch={(subscription, branch) => setModal({ type: "stopBranch", subscription, branch })}
                  onHistory={openHistory}
                  onCreateSubscription={() => setModal({ type: "create", subType: "platform", initialStoreId: row.storeId })}
                  onViewInvoice={openInvoice}
                  canGrant={canGrant}
                  grantable
                  onGrant={() => setModal({ type: "grant", initialStoreId: row.storeId })}
                  onGrantAction={(mode, subscription) => setModal({ type: "grantAction", mode, subscription })}
                />
                <ServiceBlock
                  label={text.subscriptions.ledgerLabels.intgStatus}
                  block={row.integration}
                  text={text}
                  lang={lang}
                  canManage={canManage}
                  canChange={canChange}
                  canCancel={canCancel}
                  onChangePlan={(subscription) => setModal({ type: "changePlan", subscription })}
                  onAddBranch={(subscription, branch) => setModal({ type: "addBranch", subscription, branch })}
                  onExtendBranch={(subscription, branch) => setModal({ type: "extendBranch", subscription, branch })}
                  onStopBranch={(subscription, branch) => setModal({ type: "stopBranch", subscription, branch })}
                  onHistory={openHistory}
                  onCreateSubscription={() => setModal({ type: "create", subType: "integration", initialStoreId: row.storeId })}
                  onViewInvoice={openInvoice}
                />
              </div>
            )}
          </div>
        ))
      )}

      <div className="billing-plan-footer">
        <span>
          {ledger.pagination.total} {text.common.records}
        </span>
        <div>
          <Button size="sm" appearance="outline" disabled={ledger.pagination.page <= 1} onClick={() => load({ page: ledger.pagination.page - 1 })}>
            {text.common.previous}
          </Button>
          <span>
            {text.common.page} {ledger.pagination.page} {text.common.of} {ledger.pagination.pages}
          </span>
          <Button size="sm" appearance="outline" disabled={ledger.pagination.page >= ledger.pagination.pages} onClick={() => load({ page: ledger.pagination.page + 1 })}>
            {text.common.next}
          </Button>
        </div>
      </div>

      {modal?.type === "create" && (
        <CreateSubscriptionModal
          type={modal.subType || "platform"}
          initialStoreId={modal.initialStoreId || null}
          onCancel={() => setModal(null)}
          onCreated={async () => {
            setModal(null);
            await load();
          }}
        />
      )}
      {modal?.type === "grant" && (
        <GrantSubscriptionModal
          initialStoreId={modal.initialStoreId || null}
          onCancel={() => setModal(null)}
          onGranted={async () => {
            setModal(null);
            await load();
          }}
        />
      )}
      {modal?.type === "grantAction" && (
        <GrantActionModal
          mode={modal.mode}
          subscription={modal.subscription}
          onCancel={() => setModal(null)}
          onDone={async () => {
            setModal(null);
            await load();
          }}
        />
      )}
      {modal?.type === "changePlan" && (
        <ChangePlanModal
          subscription={modal.subscription}
          onCancel={() => setModal(null)}
          onChanged={async () => {
            setModal(null);
            await load();
          }}
        />
      )}
      {modal?.type === "addBranch" && (
        <AddBranchModal
          branch={modal.branch}
          onCancel={() => setModal(null)}
          onConfirm={addBranch}
        />
      )}
      {modal?.type === "extendBranch" && (
        <ExtendCoverageModal
          title={text.subscriptions.extendBranchTitle}
          description={text.subscriptions.extendBranchDescription}
          branches={[{ branchId: modal.branch.branchId, name: modal.branch.name, currentEndDate: modal.branch.endDate }]}
          onCancel={() => setModal(null)}
          onConfirm={extendBranch}
        />
      )}
      {modal?.type === "stopBranch" && (
        <StopCoverageModal
          title={text.subscriptions.stopBranchTitle}
          message={modal.subscription.type === "platform" ? text.subscriptions.stopBranchMessageCascade : text.subscriptions.stopBranchMessage}
          branches={[{ name: modal.branch.name }]}
          confirmText={text.subscriptions.ledgerLabels.stopBranch}
          cancelText={text.common.cancel}
          onCancel={() => setModal(null)}
          onConfirm={stopBranch}
        />
      )}
      {modal?.type === "extendAll" && (
        <ExtendCoverageModal
          title={text.subscriptions.extendAllTitle}
          description={text.subscriptions.extendAllDescription}
          branches={allActiveBranches(modal.row, text)}
          onCancel={() => setModal(null)}
          onConfirm={extendAll}
        />
      )}
      {modal?.type === "stopAll" && (
        <StopCoverageModal
          title={text.subscriptions.stopAllTitle}
          message={text.subscriptions.stopAllMessage}
          branches={allActiveBranches(modal.row, text)}
          confirmText={text.subscriptions.stopAll}
          cancelText={text.common.cancel}
          onCancel={() => setModal(null)}
          onConfirm={stopAll}
        />
      )}
      {historyPanel && (
        <div className="billing-modal-backdrop">
          <div className="billing-modal">
            <h3>{text.subscriptions.ledgerLabels.viewHistory}</h3>
            {historyPanel.loading ? (
              <div className="billing-loading">{text.common.loading}</div>
            ) : historyPanel.rows.length === 0 ? (
              <div className="billing-empty-note">{text.history.empty}</div>
            ) : (
              historyPanel.rows.map((row) => (
                <div key={row.id} className="billing-history-row">
                  <strong>{row.action}</strong>
                  <span>{formatDate(row.createdAt, lang)}</span>
                  {row.reason && <small>{row.reason}</small>}
                </div>
              ))
            )}
            <div className="billing-modal-actions">
              <Button appearance="filled" onClick={() => setHistoryPanel(null)}>
                {text.common.close}
              </Button>
            </div>
          </div>
        </div>
      )}
      {detailInvoice && (
        <InvoiceDetailModal
          invoice={detailInvoice}
          onClose={() => setDetailInvoice(null)}
          onUpdated={async (updated) => {
            setDetailInvoice(updated);
            await load();
          }}
        />
      )}
      {canView && canGrant && <ReconciliationPanel />}
    </section>
  );
};

export default SubscriptionsTab;
